/**
 * Helpers pour générer des JSON-LD riches compatibles Schema.org.
 * Utilisés dans les pages via `useSeo({ jsonLd: ... })`.
 *
 * Types supportés :
 *  - Course             (formations permis moto)
 *  - FAQPage            (Questions / Réponses)
 *  - HowTo              (étapes pour réussir l'ETM, plateau...)
 *  - VideoObject        (vidéos pédagogiques)
 *  - ItemList           (liste de thèmes, modules, articles)
 *  - WebSite            (avec SearchAction → rich snippet de recherche Google)
 *  - Organization       (entité éditrice — CODE 2 ROUES / LLB ECSR)
 *  - Article            (articles magazine)
 *  - LearningResource   (contenu pédagogique générique)
 *  - Quiz               (examens blancs ETM)
 */

const SITE_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SITE_URL) ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  'https://code2roues.fr';

const ORG = {
  '@type': 'EducationalOrganization',
  '@id': `${SITE_URL}/#educational`,
  'name': 'CODE 2 ROUES',
  'alternateName': ['Code 2 Roues', 'CODE2ROUES'],
  'url': SITE_URL,
  'logo': `${SITE_URL}/logo-512.png`,
  'sameAs': [],
  'description': "Plateforme indépendante de préparation au Code Moto (ETM 2026), animée par des moniteurs ECSR — 100 % gratuite.",
};

const PROVIDER = ORG; // alias rétro-compatible

/**
 * WebSite + SearchAction (rich snippet "sitelinks search box" sur Google).
 * À insérer une seule fois sur la Home.
 */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    'url': SITE_URL,
    'name': 'CODE 2 ROUES',
    'description': 'Code Moto gratuit 2026 (ETM) — 9 thèmes, examens blancs, parcours pédagogique.',
    'inLanguage': 'fr-FR',
    'publisher': { '@id': `${SITE_URL}/#educational` },
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${SITE_URL}/revision?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Organization (entité éditrice — exposable depuis n'importe quelle page racine).
 */
export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    ...ORG,
  };
}

/**
 * Génère un Course schema pour une formation permis moto.
 * @param {Object} opts
 * @param {string} opts.name
 * @param {string} opts.description
 * @param {string} opts.path
 * @param {string} [opts.audience] – eg "Candidats permis moto 16+"
 * @param {string} [opts.duration] – eg "P7D" (ISO 8601), "PT7H"
 * @param {string} [opts.level] – eg "Beginner", "Intermediate"
 */
export function buildCourseSchema({
  name,
  description,
  path,
  audience = 'Candidats permis moto',
  duration,
  level = 'Beginner',
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    'name': name,
    'description': description,
    'url': `${SITE_URL}${path}`,
    'provider': PROVIDER,
    'isAccessibleForFree': true,
    'inLanguage': 'fr-FR',
    'educationalLevel': level,
    'audience': {
      '@type': 'EducationalAudience',
      'educationalRole': audience,
    },
    ...(duration ? { 'timeRequired': duration } : {}),
    'hasCourseInstance': {
      '@type': 'CourseInstance',
      'courseMode': 'online',
      'courseWorkload': duration || 'PT20H',
    },
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'EUR',
      'availability': 'https://schema.org/InStock',
      'category': 'Gratuit',
    },
  };
}

/**
 * Article schema pour articles magazine.
 * @param {Object} opts
 * @param {string} opts.headline       - Titre
 * @param {string} opts.description    - Résumé
 * @param {string} opts.path           - /magazine/slug
 * @param {string} [opts.image]        - URL absolue de l'image
 * @param {string} [opts.datePublished] - ISO 8601 (défaut : today)
 * @param {string} [opts.dateModified] - ISO 8601 (défaut : datePublished)
 * @param {string} [opts.author]       - Auteur(s)
 * @param {string} [opts.section]      - Catégorie éditoriale
 */
export function buildArticleSchema({
  headline,
  description,
  path,
  image,
  datePublished,
  dateModified,
  author = 'Équipe CODE 2 ROUES — Moniteurs ECSR',
  section,
}) {
  const pub = datePublished || new Date().toISOString().split('T')[0];
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${path}`,
    },
    'headline': headline,
    'description': description,
    ...(image ? { 'image': [image] } : {}),
    'datePublished': pub,
    'dateModified': dateModified || pub,
    'author': {
      '@type': 'Organization',
      'name': author,
      'url': SITE_URL,
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'CODE 2 ROUES',
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_URL}/logo-512.png`,
      },
    },
    'isAccessibleForFree': true,
    'inLanguage': 'fr-FR',
    ...(section ? { 'articleSection': section } : {}),
  };
}

/**
 * LearningResource — type Schema.org pour contenu pédagogique structuré.
 * Très utile pour les pages /revision, /programme-etm, /lexique.
 */
export function buildLearningResourceSchema({
  name,
  description,
  path,
  educationalLevel = 'Beginner',
  learningResourceType = 'Quiz',
  audience = 'Candidats permis moto',
  about,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    'name': name,
    'description': description,
    'url': `${SITE_URL}${path}`,
    'inLanguage': 'fr-FR',
    'educationalLevel': educationalLevel,
    'learningResourceType': learningResourceType,
    'isAccessibleForFree': true,
    'audience': {
      '@type': 'EducationalAudience',
      'educationalRole': audience,
    },
    'publisher': PROVIDER,
    ...(about ? { 'about': about } : {}),
  };
}

/**
 * VideoObject schema — pour vidéos pédagogiques officielles intégrées.
 */
export function buildVideoSchema({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  contentUrl,
  embedUrl,
  duration,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    'name': name,
    'description': description,
    'thumbnailUrl': Array.isArray(thumbnailUrl) ? thumbnailUrl : [thumbnailUrl],
    'uploadDate': uploadDate,
    ...(contentUrl ? { 'contentUrl': contentUrl } : {}),
    ...(embedUrl ? { 'embedUrl': embedUrl } : {}),
    ...(duration ? { 'duration': duration } : {}),
  };
}

/**
 * Génère une liste d'items (ItemList) schema — utile pour /formation, /lexique, /revision.
 */
export function buildItemList({ name, items }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': name,
    'numberOfItems': items.length,
    'itemListElement': items.map((it, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'name': it.name,
      'url': it.path ? `${SITE_URL}${it.path}` : undefined,
      'description': it.description,
    })),
  };
}

/**
 * Génère un FAQ schema.
 * @param {Array<{q: string, a: string}>} items
 */
export function buildFaqSchema(items = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': items.map(({ q, a }) => ({
      '@type': 'Question',
      'name': q,
      'acceptedAnswer': { '@type': 'Answer', 'text': a },
    })),
  };
}

/**
 * Génère un HowTo schema (guide étape par étape).
 */
export function buildHowToSchema({ name, description, totalTime, steps, image }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': name,
    'description': description,
    ...(image ? { 'image': Array.isArray(image) ? image : [image] } : {}),
    ...(totalTime ? { 'totalTime': totalTime } : {}),
    'step': steps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': s.name,
      'text': s.text,
      ...(s.image ? { 'image': s.image } : {}),
      ...(s.url ? { 'url': s.url } : {}),
    })),
  };
}
